<script lang="ts">
import AspectRatio from '@monogrid/vue-boost/core/AspectRatio.vue'
import { useModal } from '@monogrid/vue-boost/integrations/focus-trap'
import { templateRef } from '@vueuse/core'
import { gsap } from 'gsap'
import { computed, defineComponent, inject, nextTick, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { settingsInjectionKey } from '@/App.vue'
import CtaPrimary from '@/components/atoms/CtaPrimary.vue'
import CtaSecondary from '@/components/atoms/CtaSecondary.vue'
import IconComponent from '@/components/atoms/IconComponent.vue'
import NavItem from '@/components/molecules/NavItem.vue'
import SocialLinks, { type SocialLink } from '@/components/molecules/SocialLinks.vue' // eslint-disable-line
import VideoLauncher from '@/components/molecules/VideoLauncher.vue'
import { useBreakpoint } from '@/functions/useBreakpoint'
import { getFullYear, groupBy } from '@/utils'

const menu = [
  { label: 'Home', route: { name: 'Home' } },
  { label: 'About Us', route: { name: 'About' } },
  { label: 'Projects', route: { name: 'Works' } },
  { label: 'Universe', route: { name: 'Universe' } },
  { label: 'Contact Us', route: { name: 'Contact' } }
]

export default defineComponent({
  name: 'SiteMenu',
  components: {
    AspectRatio,
    CtaPrimary,
    CtaSecondary,
    IconComponent,
    NavItem,
    SocialLinks,
    VideoLauncher
  },
  emits: ['open', 'close'],
  setup (props, { attrs, slots, emit, expose }) {
    const breakpoint = useBreakpoint()
    const route = useRoute()
    const wrapper = templateRef<HTMLElement>('wrapper')
    const panel = templateRef<HTMLElement>('panel')
    const darkener = templateRef<HTMLElement>('darkener')
    const visible = ref(false)
    const revealed = ref(false)
    const settings = inject(settingsInjectionKey)
    let timeline: gsap.core.Timeline

    const offices = computed(() => {
      if (!settings.value?.offices) return []
      const groups = groupBy(settings.value?.offices, 'mail')
      return Object.values(groups).reduce((acc, group) => {
        acc.push({ ...group[0], city: group.map(i => i.city).sort().join('/') })
        return acc
      }, [])
    })
    const open = async () => {
      if (visible.value) return

      visible.value = true

      await nextTick()

      const columns: HTMLElement[] = gsap.utils.toArray('.menu-column', panel.value)

      if (breakpoint.lg) columns.sort((a, b) => parseInt(a.dataset.order) - parseInt(b.dataset.order))

      timeline = gsap.timeline()

      timeline.from(darkener.value, {
        autoAlpha: 0,
        duration: 1,
        ease: 'power3.inOut'
      })

      timeline.from(panel.value, {
        yPercent: -100,
        duration: 1.2,
        ease: 'power4.inOut'
      }, '<')

      timeline.add(() => (revealed.value = !revealed.value), '<+=0.8')

      return timeline
    }

    const close = async () => {
      if (!timeline || !visible.value) return

      await timeline.timeScale(1.5).reverse()

      timeline.kill()
      timeline = undefined

      visible.value = false
    }

    const toggle = () => visible.value ? close() : open()

    useModal(wrapper, close, { allowOutsideClick: true, initialFocus: false })

    watch(() => route.name, () => {
      visible.value && close()
    })

    onUnmounted(() => timeline?.kill())

    expose({
      open,
      close,
      toggle
    })

    return {
      menu,
      revealed,
      settings,
      visible,
      offices,
      open,
      close,
      toggle,
      getFullYear,
      onIubendaClick: () => window?._iub?.cs?.api?.openPreferences()
    }
  }
})
</script>

<template>
  <div>
    <CtaPrimary class="w-13 justify-center" green @click="toggle">
      <Transition mode="out-in">
        <span v-if="visible" class="uppercase">{{ $t('Close') }}</span>
        <span v-else>Menu</span>
      </Transition>
    </CtaPrimary>
    <Teleport to="body">
      <div v-if="visible" ref="wrapper" class="menu fixed inset-0 overflow-hidden">
        <div ref="darkener" class="darkener absolute inset-0 z-0" @click.self="close" />
        <div
          ref="panel"
          class="panel pt-safe-header bg-blue max-h-full overflow-y-auto"
          data-lenis-prevent
        >
          <div class="pt-5 pb-6 px-3 lg:px-10 lg:pb-10 lg:grid lg:grid-cols-3 lg:gap-11">
            <div
              v-reveal.group="'slideUp'"
              v-reveal:toggle="revealed"
              v-reveal:group-options="{ stagger: 0.05 }"
              v-reveal:options="{ duration: 0.8, ease: 'power3.out', stagger: { amount: 0.3 } }"
              class="menu-column"
              data-order="1"
            >
              <div class="relative overflow-hidden">
                <p v-reveal.child class="menu-item pb-1">
                  <IconComponent class="w-3 mr-2" icon="segment" />
                  <span class="label1">MENU</span>
                </p>
                <div
                  v-reveal.child="'scaleX'"
                  v-reveal:options="{ delay: 0.1 }"
                  class="border-horizontal origin-left bg-white:50 bottom-0"
                />
              </div>
              <nav class="mt-6">
                <ul>
                  <li v-for="item in menu" :key="item.route.name" class="overflow-hidden">
                    <div v-reveal.child class="menu-item">
                      <CtaSecondary
                        class="h3"
                        :class="{ 'active pointer-events-none': $route.name === item.route.name }"
                        :to="item.route"
                        @click="close"
                      >
                        {{ item.label }}
                      </CtaSecondary>
                    </div>
                  </li>
                </ul>
              </nav>
            </div>
            <div
              v-reveal.group="'slideUp'"
              v-reveal:toggle="revealed"
              v-reveal:group-options="{ delay: 0.2, stagger: 0.05 }"
              v-reveal:options="{ duration: 0.8, ease: 'power3.out', stagger: { amount: 0.3 } }"
              class="menu-column lt-lg:mt-6 lg:flex lg:flex-col lg:justify-between lg:order-3"
              data-order="3"
            >
              <div>
                <div class="relative overflow-hidden">
                  <p v-reveal.child class="menu-item pb-1">
                    <IconComponent class="w-3 mr-2" icon="segment" />
                    <span class="label1 uppercase">{{ $t('Contact') }}</span>
                  </p>
                  <div
                    v-reveal.child="'scaleX'"
                    v-reveal:options="{ delay: 0.1 }"
                    class="border-horizontal origin-left bg-white:50 bottom-0"
                  />
                </div>
                <div class="flex flex-col mt-6 gap-3">
                  <div v-for="office, i in offices" :key="i" class="opacity-75 overflow-hidden">
                    <p v-reveal.child class="menu-item">
                      <span>{{ office.city }}, {{ office.country }}</span>
                      <span class="inline-block px-1 lt-md:py-0.5">—</span>
                      <CtaSecondary
                        class="menu-item opacity-75"
                        :href="`mailto:${office.mail}`"
                        underline
                      >
                        {{ office.mail }}
                      </CtaSecondary><br class="md:hidden" />
                    </p>
                  </div>
                  <div v-if="settings.jobsEmail" class="opacity-75 overflow-hidden">
                    <p v-reveal.child class="menu-item">
                      <span>{{ $t('Jobs') }}</span>
                      <span class="inline-block px-1 lt-md:py-0.5">—</span>
                      <CtaSecondary
                        class="menu-item opacity-75"
                        :href="`mailto:${settings.jobsEmail}`"
                        underline
                      >
                        {{ settings.jobsEmail }}
                      </CtaSecondary>
                    </p>
                  </div>
                </div>
                <div class="overflow-hidden">
                  <SocialLinks
                    v-if="settings.socialLinks?.length"
                    v-reveal.child
                    class="mt-6"
                    link-class="menu-item"
                    :links="(settings.socialLinks as SocialLink[])"
                  />
                </div>
                <!-- <div class="flex gap-5 overflow-hidden" :class="settings.socialLinks?.length ? 'mt-4' : 'mt-6'">
                  <CtaSecondary
                    v-if="settings.genericEmail"
                    v-reveal.child
                    class="menu-item opacity-75"
                    :href="`mailto:${settings.genericEmail}`"
                    underline
                  >
                    {{ settings.genericEmail }}
                  </CtaSecondary>
                  <CtaSecondary
                    v-if="settings.jobsEmail"
                    v-reveal.child
                    class="menu-item opacity-75"
                    :href="`mailto:${settings.jobsEmail}`"
                    underline
                  >
                    {{ settings.jobsEmail }}
                  </CtaSecondary>
                </div> -->
              </div>
              <div v-if="settings.showreel?.video" class="mt-6 overflow-hidden">
                <AspectRatio
                  v-reveal.child="'fadeIn'"
                  v-reveal:options="{ delay: 0.2 }"
                  class="menu-item bg-black"
                  :ratio="16/9"
                >
                  <VideoLauncher
                    class="w-full h-full"
                    :webgl="false"
                    :video="settings.showreel.video"
                    :video-mobile="settings.showreelMobile"
                    :preview-media="settings.showreel.poster"
                  />
                </AspectRatio>
              </div>
            </div>
            <div
              v-reveal.group="'slideUp'"
              v-reveal:toggle="revealed"
              v-reveal:group-options="{ delay: 0.1, stagger: 0.05 }"
              v-reveal:options="{ duration: 0.8, ease: 'power3.out', stagger: { amount: 0.3 } }"
              class="menu-column lt-lg:mt-6 lg:flex lg:flex-col lg:justify-between lg:order-2"
              data-order="2"
            >
              <div>
                <div class="relative overflow-hidden">
                  <p v-reveal.child class="menu-item pb-1">
                    <IconComponent class="w-3 mr-2" icon="segment" />
                    <span class="label1 uppercase">{{ $t('External') }}</span>
                  </p>
                  <div
                    v-reveal.child="'scaleX'"
                    v-reveal:options="{ delay: 0.1 }"
                    class="border-horizontal origin-left bg-white:50 bottom-0"
                  />
                </div>
                <div class="mt-6 lg:flex lg:flex-col lg:justify-between">
                  <nav>
                    <ul>
                      <li v-for="link in settings.externalLinks" :key="link.url" class="overflow-hidden">
                        <div v-reveal.child class="menu-item">
                          <NavItem
                            :href="link.url"
                            target="_blank"
                            rel="nofollow noopener"
                            @click="close"
                          >
                            {{ link.label }}
                          </NavItem>
                        </div>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
              <div class="mt-6 lg:flex lg:flex-col lg:justify-end">
                <div class="pt-4 lg:pt-9 overflow-hidden relative">
                  <div
                    v-reveal.child
                    class="menu-item pb-4"
                    :class="{ 'flex items-center justify-between': $breakpoints.xl || ($breakpoints.sm && $breakpoints.ltLg) }"
                  >
                    <p class="h5 pr-4 lt-xl:mb-2 case-upper">{{ settings.newsletterTitle }}</p>
                    <CtaPrimary
                      green
                      :class="{
                        'w-full': $breakpoints.ltSm || ($breakpoints.lg && $breakpoints.ltXl),
                        'flex-shrink-0': $breakpoints.xl || ($breakpoints.sm && $breakpoints.ltLg)
                      }"
                      :to="{ name: 'Newsletter' }"
                      @click="close"
                    >
                      {{ settings.newsletterCtaLabel || 'Subscribe' }}
                    </CtaPrimary>
                  </div>
                  <div
                    v-reveal.child="'scaleX'"
                    v-reveal:options="{ delay: 0.1 }"
                    class="border-horizontal origin-left bg-white:50 bottom-0"
                  />
                </div>
                <div class="mt-6 label1">
                  <div class="flex gap-5 overflow-hidden">
                    <CtaSecondary
                      v-if="settings.privacyPolicy?.file"
                      v-reveal.child
                      class="menu-item opacity-75"
                      :href="`/media/${ settings.privacyPolicy.file.id }`"
                      target="_blank"
                      rel="nofollow noopener"
                    >
                      {{ settings.privacyPolicy.label || 'Privacy Policy' }}
                    </CtaSecondary>
                    <CtaSecondary
                      v-if="settings.cookiePolicy?.url"
                      v-reveal.child
                      class="menu-item opacity-75"
                      :href="settings.cookiePolicy.url"
                      target="_blank"
                      rel="nofollow noopener"
                    >
                      {{ settings.cookiePolicy.label || 'Cookie Policy' }}
                    </CtaSecondary>
                    <CtaSecondary
                      v-reveal.child
                      class="menu-item opacity-75"
                      @click="onIubendaClick"
                    >
                      {{ $t('Cookie settings') }}
                    </CtaSecondary>
                  </div>
                  <div class="mt-3 overflow-hidden opacity-75">
                    <p v-reveal.child class="menu-item">©{{ getFullYear() }} ALL COPYRIGHTS RESERVED BY MONOGRID</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.menu {
  z-index: $z-menu;
}

.darkener {
  background-color: rgba(color('black'), 95%);
}

a[class*="opacity-"], button[class*="opacity-"] {
  @include hover {
    transition: color var(--transition-duration), opacity var(--transition-duration);

    &:hover {
      opacity: 1;
    }
  }
}

nav {
  li:nth-child(n+2) {
    margin-top: rem(8px);

    @screen lg {
      margin-top: rem(12px);
    }
  }

  a {
    transition: transform var(--transition-duration), color var(--transition-duration);

    &.active {
      color: color('black');
    }

    @include hover {
      &:hover {
        color: color('black');
      }
    }
  }

  .menu-column:first-child & a {
    padding-right: rem(28px);

    @screen lg {
      padding-right: rem(32px);
    }

    @screen xl {
      padding-right: rem(60px);
    }

    &::before {
      position: absolute;
      top: 50%;
      right: 100%;
      width: rem(12px);
      height: rem(12px);
      background-color: color('green');
      content: '';
      transform: translate(rem(-8px), -100%);
      transition: opacity $default-duration;

      @screen lg {
        transform: translate(rem(-16px), -100%);
      }

      @screen xl {
        transform: translate(rem(-24px), -100%);
      }
    }

    &.active {
      transform: translate3d(rem(28px), 0, 0);

      @screen lg {
        transform: translate3d(rem(32px), 0, 0);
      }

      @screen xl {
        transform: translate3d(rem(60px), 0, 0);
      }
    }
  }
}
</style>
